/**
 * "Where is my application?" — and, when we have asked for something, the place
 * to send it.
 *
 * Reached with no account, using the reference from the acknowledgement email.
 * The server tells this page as little as it can: a state, two dates, and — only
 * when SmartSpaza is waiting — what was asked for. There is deliberately no name,
 * no address and no contact detail in the response, because a reference travels
 * in forwarded emails and must not become a lookup for somebody's mobile number.
 *
 * The reference lives in the query string rather than in component state so the
 * page can be bookmarked and reopened, which is what somebody who is waiting
 * actually does with it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  Button,
  Pill,
  SelectField,
  TextField,
  type PillTone,
} from "@/components/ui";
import {
  applicationRepository,
  type ApplicationStatus,
  type DocumentKind,
} from "@/repositories/applicationRepository";

import { Shell } from "./ApplyPage";

const TONES: Record<ApplicationStatus, PillTone> = {
  APPLICATION: "neutral",
  PENDING_REVIEW: "info",
  PENDING_DOCUMENTS: "warn",
  APPROVED: "success",
  REJECTED: "danger",
};

/**
 * What each state means, in the applicant's terms rather than the system's.
 *
 * "PENDING_REVIEW" is accurate and tells a shopkeeper nothing about whether they
 * need to do something, which is the only question they have.
 */
const EXPLANATIONS: Record<ApplicationStatus, string> = {
  APPLICATION: "Your application has been started but not sent to us yet.",
  PENDING_REVIEW:
    "We have your application and are looking at it. There is nothing to do.",
  PENDING_DOCUMENTS: "We need something more from you before we can finish.",
  APPROVED:
    "You are approved. Look out for an email with your sign-in details.",
  REJECTED:
    "We were not able to approve this application. Our email explains why.",
};

const DOCUMENT_KINDS: { value: DocumentKind; label: string }[] = [
  {
    value: "BUSINESS_REGISTRATION",
    label: "Business registration certificate",
  },
  { value: "TAX_CLEARANCE", label: "Tax clearance or VAT certificate" },
  { value: "PROOF_OF_ADDRESS", label: "Proof of the shop's address" },
  { value: "BANK_CONFIRMATION", label: "Bank account confirmation" },
  { value: "TRADING_LICENCE", label: "Trading or health licence" },
  { value: "OTHER", label: "Something else" },
];

export function ApplicationStatusPage() {
  const [params, setParams] = useSearchParams();
  const reference = (params.get("reference") ?? "").trim();
  const [typed, setTyped] = useState(reference);

  const progress = useQuery({
    queryKey: ["application", reference],
    queryFn: () => applicationRepository.progress(reference),
    enabled: reference.length > 0,
    retry: false,
  });

  const notFound =
    progress.error instanceof ApiError && progress.error.status === 404;

  if (!reference || notFound) {
    return (
      <Shell
        title="Check on your application"
        lead="Enter the reference we sent you — it looks like APP-2026-000123."
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            setParams(typed.trim() ? { reference: typed.trim() } : {});
          }}
        >
          {notFound && (
            <p className="rounded-card bg-danger-50 px-3 py-2 text-sm text-danger-700">
              We could not find an application with that reference. Check it
              against the email we sent you.
            </p>
          )}
          <TextField
            label="Your reference"
            value={typed}
            autoFocus
            placeholder="APP-2026-000123"
            onChange={(event) => setTyped(event.target.value)}
          />
          <Button type="submit" variant="primary">
            Check
          </Button>
        </form>
      </Shell>
    );
  }

  if (progress.isLoading || !progress.data) {
    return (
      <Shell title="Check on your application" lead="One moment.">
        <p className="text-sm text-ink-500">Looking it up…</p>
      </Shell>
    );
  }

  const application = progress.data;

  return (
    <Shell
      title="Your application"
      lead="This page updates as we work through it. You can come back to it any time."
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="select-all font-mono text-lg font-semibold text-ink-900">
            {application.reference}
          </p>
          <Pill tone={TONES[application.status]}>
            {application.status_label}
          </Pill>
        </div>

        <p className="text-sm text-ink-600">
          {EXPLANATIONS[application.status]}
        </p>

        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-ink-500">Sent to us</dt>
            <dd className="text-ink-900">
              {application.submitted_at?.slice(0, 10) ?? "—"}
            </dd>
          </div>
          <div>
            <dt className="text-ink-500">Looked at</dt>
            <dd className="text-ink-900">
              {application.reviewed_at?.slice(0, 10) ?? "Not yet"}
            </dd>
          </div>
        </dl>

        {application.status === "PENDING_DOCUMENTS" && (
          <WaitingOnYou
            reference={application.reference}
            asked={application.information_requested}
          />
        )}
      </div>
    </Shell>
  );
}

/**
 * The only part of this page that does anything.
 *
 * Uploading and telling us you are done are two separate acts on purpose: a
 * shopkeeper photographing three certificates should not have the application
 * bounce back into the queue after the first one, and a reviewer should not pick
 * it up half-supplied.
 */
function WaitingOnYou({
  reference,
  asked,
}: {
  reference: string;
  asked: string;
}) {
  const client = useQueryClient();
  const [kind, setKind] = useState<DocumentKind>("BUSINESS_REGISTRATION");
  const [sent, setSent] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) =>
      applicationRepository.uploadDocument(reference, file, kind),
    onSuccess: (document) => {
      setError(null);
      setSent((previous) => [
        ...previous,
        document.original_name || document.kind_label,
      ]);
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? caught.detail
          : "That file could not be sent. Please try again.",
      ),
  });

  const done = useMutation({
    mutationFn: () =>
      applicationRepository.resubmit(reference, "Sent what you asked for."),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: ["application"] }),
  });

  return (
    <div className="space-y-4 rounded-card border border-warn-500/40 bg-warn-50/50 p-4">
      <div>
        <h2 className="text-sm font-semibold text-ink-900">What we need</h2>
        <p className="mt-1 text-sm text-ink-600">
          {asked || "Please send the documents we asked for by email."}
        </p>
      </div>

      {error && (
        <p className="rounded-card bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </p>
      )}

      <SelectField
        label="What is this document?"
        options={DOCUMENT_KINDS}
        value={kind}
        onChange={(event) => setKind(event.target.value as DocumentKind)}
      />

      <label className="block">
        <span className="text-sm font-medium text-ink-900">Choose a file</span>
        <span className="mt-0.5 block text-sm text-ink-500">
          A PDF or a photograph, under 8 MB. A picture taken on your phone is
          fine.
        </span>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/*"
          disabled={upload.isPending}
          className="mt-2 block w-full text-sm text-ink-600 file:mr-3 file:h-9 file:rounded-control file:border file:border-line file:bg-surface file:px-3 file:text-sm"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) upload.mutate(file);
            // Cleared so choosing the same file twice fires a change event —
            // which somebody re-sending a photograph after a failure will do.
            event.target.value = "";
          }}
        />
      </label>

      {sent.length > 0 && (
        <div>
          <p className="text-sm font-medium text-ink-900">Sent</p>
          <ul className="mt-1 space-y-0.5 text-sm text-ink-600">
            {sent.map((name, index) => (
              <li key={`${name}-${index}`}>✓ {name}</li>
            ))}
          </ul>
        </div>
      )}

      <Button
        variant="primary"
        disabled={sent.length === 0 || done.isPending}
        loading={done.isPending}
        onClick={() => done.mutate()}
      >
        I have sent everything
      </Button>
    </div>
  );
}
